/**
 * FlahaSOIL v2 — agronomic copy snippets.
 *
 * Short, plain-language captions displayed under technical numbers
 * and interpretation labels so a non-specialist user can understand
 * what each value means in practice. These are advisory snippets
 * only — no values are computed here and no thresholds are encoded
 * (the classifiers in `@flaha/soil-interpretation` own that).
 *
 * Progressive help: *HELP = always-visible one-liners;
 * *METHOD = optional expanded method context behind "Method".
 */

/** One-line description per interpretation field, keyed by DTO field name. */
export const INTERPRETATION_FIELD_HELP: Record<string, string> = {
	phCategory:
		"Governs how available nutrients are to roots.",
	salinityRisk:
		"Higher soluble salts make it harder for plants to take up water.",
	cecLevel:
		"Higher CEC means the soil can hold more plant nutrients.",
	baseSaturationCategory:
		"Share of exchange sites held by useful bases (Ca, Mg, K, Na).",
	cationBalance:
		"Balance among Ca, Mg and K supports steady nutrient uptake.",
	sodiumRisk:
		"Excess exchangeable sodium can weaken structure and slow infiltration.",
	waterHoldingClass:
		"How much plant-usable water the profile can store between irrigations.",
	drainageClass:
		"How freely water moves through the profile after wetting.",
};

/** Optional method detail for interpretation fields (expand on demand). */
export const INTERPRETATION_FIELD_METHOD: Record<string, string> = {
	phCategory:
		"Classified from laboratory pH. Near-neutral (about 6.5\u20137.5) suits most crops; extremes limit nutrient availability.",
	salinityRisk:
		"Classified from electrical conductivity (EC). Higher EC reduces water uptake and crop establishment.",
	cecLevel:
		"Cation exchange capacity (cmol(+)/kg) reflects nutrient buffering capacity.",
	baseSaturationCategory:
		"Base saturation is the share of CEC occupied by Ca, Mg, K and Na. Mid-range values are typically optimal.",
	cationBalance:
		"Derived from relative cation shares. Excess of any one cation can suppress the others.",
	sodiumRisk:
		"Based on exchangeable sodium percentage (ESP). Values above ~15 % often degrade structure.",
	waterHoldingClass:
		"Plant-available water capacity from physics outputs (field capacity minus wilting point).",
	drainageClass:
		"Inferred from texture and conductivity. Poor drainage risks waterlogging; very rapid drainage risks leaching.",
};

/** Short caption per overall rating. */
export const OVERALL_RATING_HELP: Record<string, string> = {
	GOOD: "Soil conditions are generally favourable for the typical crop range.",
	FAIR: "Soil is workable but has one or more limiting factors worth managing.",
	POOR: "One or more major constraints are present; targeted remediation is advised.",
};

/** Per-physics-number captions for the results page. */
export const PHYSICS_HELP: Record<string, string> = {
	textureClass: "USDA class from the sand / silt / clay you entered.",
	fieldCapacity: "Upper limit of plant-usable water after free drainage.",
	wiltingPoint: "Moisture level where most crops can no longer extract water.",
	plantAvailableWater: "Water the crop can actually use between FC and WP.",
	saturation: "Total pore space when the soil is fully wet.",
	saturatedConductivity: "How fast water moves when the soil is saturated.",
	bulkDensity: "Dry mass per volume \u2014 high values often mean compaction.",
};

export const PHYSICS_METHOD: Record<string, string> = {
	textureClass:
		"Classified with the USDA Soil Texture Triangle from sand, silt and clay percentages.",
	fieldCapacity:
		"Estimated moisture (vol %) two to three days after wetting \u2014 model estimate unless lab-measured.",
	wiltingPoint:
		"Estimated soil moisture (vol %) at which most crops stop extracting water (Saxton & Rawls where applied).",
	plantAvailableWater:
		"Difference between field capacity and wilting point (vol %). Model estimate for irrigation planning.",
	saturation:
		"Total porosity (vol %); upper bound of water the soil can hold when fully wet.",
	saturatedConductivity:
		"Estimated saturated hydraulic conductivity (mm/h). High values can mean leaching; very low values can mean ponding.",
	bulkDensity:
		"Dry mass per unit volume (g/cm\u00B3). High values often indicate compaction that limits roots.",
};

/** Per-chemistry-number captions for the results page. */
export const CHEMISTRY_HELP: Record<string, string> = {
	cec: "Nutrient-holding capacity of the soil.",
	baseSaturation: "Share of CEC held by Ca, Mg, K and Na.",
	esp: "Exchangeable sodium share of CEC.",
	sar: "Sodium risk in the soil solution.",
	cationShare: "How Ca, Mg, K and Na share the exchange complex.",
};

export const CHEMISTRY_METHOD: Record<string, string> = {
	cec: "Cation exchange capacity in cmol(+)/kg. Higher values buffer nutrients more strongly.",
	baseSaturation:
		"Share of CEC occupied by Ca, Mg, K and Na. Mid-range values are typically optimal.",
	esp: "Exchangeable sodium percentage. Above ~15 % structural problems are common.",
	sar: "Sodium adsorption ratio of the soil solution. High SAR signals sodicity risk.",
	cationShare:
		"Share of CEC held by each major cation. Imbalance can suppress nutrient uptake.",
};
